import { z } from 'zod';
/**
 * Major applications, pages, and workflows that a business or role can enter.
 *
 * Product areas intentionally describe navigation-level access. Feature-system
 * availability belongs in settings, while role operations belong in
 * `BusinessActionEnum`.
 */
export var BusinessProductAreaV2;
(function (BusinessProductAreaV2) {
    BusinessProductAreaV2["DASHBOARD"] = "DASHBOARD";
    BusinessProductAreaV2["POINT_OF_SALE"] = "POINT_OF_SALE";
    BusinessProductAreaV2["POINT_OF_SALE_V3"] = "POINT_OF_SALE_V3";
    BusinessProductAreaV2["POINT_OF_SALE_RESTAURANT"] = "POINT_OF_SALE_RESTAURANT";
    BusinessProductAreaV2["TRANSACTIONS"] = "TRANSACTIONS";
    BusinessProductAreaV2["TRANSACTIONS_V4"] = "TRANSACTIONS_V4";
    BusinessProductAreaV2["INVENTORY"] = "INVENTORY";
    BusinessProductAreaV2["INVENTORY_V4"] = "INVENTORY_V4";
    BusinessProductAreaV2["QUICK_PAY_V4"] = "QUICK_PAY_V4";
    BusinessProductAreaV2["REPORTS"] = "REPORTS";
    /** Time Clock and timesheets availability; employee/owner checks still apply. */
    BusinessProductAreaV2["TIME_CLOCK"] = "TIME_CLOCK";
    BusinessProductAreaV2["PAYMENTS"] = "PAYMENTS";
    BusinessProductAreaV2["INVOICES"] = "INVOICES";
    BusinessProductAreaV2["MARKETPLACE"] = "MARKETPLACE";
    BusinessProductAreaV2["DEBT_TRACKER"] = "DEBT_TRACKER";
    BusinessProductAreaV2["NOTIFICATIONS"] = "NOTIFICATIONS";
    BusinessProductAreaV2["SETTINGS"] = "SETTINGS";
    BusinessProductAreaV2["ADMIN"] = "ADMIN";
    BusinessProductAreaV2["PLATFORM_OPS"] = "PLATFORM_OPS";
    BusinessProductAreaV2["ORDERS"] = "ORDERS";
    BusinessProductAreaV2["CUSTOMERS"] = "CUSTOMERS";
    BusinessProductAreaV2["CASH_APP"] = "CASH_APP";
    BusinessProductAreaV2["MENU"] = "MENU";
    /** Payforms (public experiences) merchant pages: forms, submissions, customer directory. */
    BusinessProductAreaV2["PAYFORMS"] = "PAYFORMS";
    // Mobile App Areas
    BusinessProductAreaV2["MOBILE_INVENTORY"] = "MOBILE_INVENTORY";
    BusinessProductAreaV2["MOBILE_MENU"] = "MOBILE_MENU";
    BusinessProductAreaV2["MOBILE_ONE_TOUCH"] = "MOBILE_ONE_TOUCH";
    BusinessProductAreaV2["MOBILE_PAY_TAB"] = "MOBILE_PAY_TAB";
    BusinessProductAreaV2["MOBILE_REPORTS_TAB"] = "MOBILE_REPORTS_TAB";
    BusinessProductAreaV2["MOBILE_PAYOUTS"] = "MOBILE_PAYOUTS";
    BusinessProductAreaV2["MOBILE_SALES"] = "MOBILE_SALES";
    BusinessProductAreaV2["MOBILE_HOME_TAB"] = "MOBILE_HOME_TAB";
    BusinessProductAreaV2["TERMINAL_REPORTS_TAB"] = "TERMINAL_REPORTS_TAB";
    BusinessProductAreaV2["TERMINAL_SETTINGS_TAB"] = "TERMINAL_SETTINGS_TAB";
})(BusinessProductAreaV2 || (BusinessProductAreaV2 = {}));
/**
 * Role-authorized operations and flows. Keeping them in one bucket makes
 * effective authorization a simple intersection between the business ceiling
 * and the role grants.
 */
export var BusinessActionEnum;
(function (BusinessActionEnum) {
    BusinessActionEnum["INVENTORY_TOOLS"] = "INVENTORY_TOOLS";
    BusinessActionEnum["RESTAURANT_MEAL_FLOW"] = "RESTAURANT_MEAL_FLOW";
    BusinessActionEnum["RESTAURANT_KDS"] = "RESTAURANT_KDS";
    BusinessActionEnum["SALE_REFUND"] = "SALE_REFUND";
    BusinessActionEnum["SALE_PARTIAL_REFUND"] = "SALE_PARTIAL_REFUND";
    BusinessActionEnum["SALE_VOID"] = "SALE_VOID";
    BusinessActionEnum["SALE_EDIT"] = "SALE_EDIT";
    BusinessActionEnum["SALE_DELETE"] = "SALE_DELETE";
    BusinessActionEnum["SPLIT_PAYMENT_USE"] = "SPLIT_PAYMENT_USE";
    BusinessActionEnum["TIP_ADD"] = "TIP_ADD";
    BusinessActionEnum["CUSTOMER_PROFILE_USE"] = "CUSTOMER_PROFILE_USE";
    BusinessActionEnum["REPORT_VIEW"] = "REPORT_VIEW";
    BusinessActionEnum["REPORT_EXPORT"] = "REPORT_EXPORT";
    BusinessActionEnum["PRINT_DAILY_REPORT"] = "PRINT_DAILY_REPORT";
    BusinessActionEnum["CASH_DRAWER_OPEN"] = "CASH_DRAWER_OPEN";
    BusinessActionEnum["TRANSACTIONS_VIEW"] = "TRANSACTIONS_VIEW";
    BusinessActionEnum["TAX_CHANGE"] = "TAX_CHANGE";
    BusinessActionEnum["DEVICE_PAIR_APPROVE"] = "DEVICE_PAIR_APPROVE";
    BusinessActionEnum["BUSINESS_DEVICES_MANAGE"] = "BUSINESS_DEVICES_MANAGE";
    BusinessActionEnum["SETTINGS_VIEW"] = "SETTINGS_VIEW";
    BusinessActionEnum["SETTINGS_MANAGE"] = "SETTINGS_MANAGE";
    BusinessActionEnum["INVENTORY_MANAGE"] = "INVENTORY_MANAGE";
    BusinessActionEnum["PAYMENT_CREATE"] = "PAYMENT_CREATE";
    BusinessActionEnum["EMPLOYEE_MANAGE"] = "EMPLOYEE_MANAGE";
    BusinessActionEnum["USER_MANAGE"] = "USER_MANAGE";
    BusinessActionEnum["NOTIFICATION_CONTACTS_MANAGE"] = "NOTIFICATION_CONTACTS_MANAGE";
    BusinessActionEnum["NOTIFICATIONS_ALERTS_MANAGE"] = "NOTIFICATIONS_ALERTS_MANAGE";
    BusinessActionEnum["ORDER_VIEW"] = "ORDER_VIEW";
    BusinessActionEnum["ORDER_ACCEPT"] = "ORDER_ACCEPT";
    BusinessActionEnum["ORDER_COMPLETE"] = "ORDER_COMPLETE";
    /** Create, edit, publish and disable Payforms. */
    BusinessActionEnum["PAYFORM_MANAGE"] = "PAYFORM_MANAGE";
    /** Cancel a customer's recurring payment (Payforms subscription). */
    BusinessActionEnum["RECURRING_PAYMENT_CANCEL"] = "RECURRING_PAYMENT_CANCEL";
})(BusinessActionEnum || (BusinessActionEnum = {}));
/** Payment rails that the business and a role are allowed to use. */
export var BusinessPaymentMethodV2;
(function (BusinessPaymentMethodV2) {
    BusinessPaymentMethodV2["CASH"] = "CASH";
    BusinessPaymentMethodV2["CARD"] = "CARD";
    BusinessPaymentMethodV2["MANUAL_CARD"] = "MANUAL_CARD";
    BusinessPaymentMethodV2["ACH"] = "ACH";
    BusinessPaymentMethodV2["EBT_FOOD"] = "EBT_FOOD";
    BusinessPaymentMethodV2["EBT_CASH"] = "EBT_CASH";
    BusinessPaymentMethodV2["WIC"] = "WIC";
    BusinessPaymentMethodV2["DEBT"] = "DEBT";
    BusinessPaymentMethodV2["BNPL"] = "BNPL";
})(BusinessPaymentMethodV2 || (BusinessPaymentMethodV2 = {}));
/**
 * Determines when role and PIN authorization is evaluated.
 *
 * - BUSINESS_ONLY: only the business access ceiling is evaluated.
 * - USER_REQUIRED: an active user must identify with their PIN.
 * - HYBRID: the fallback role may cover normal work, while sensitive actions
 *   requires an active user to identify with their PIN.
 */
export var BusinessAccessPolicyModeV2;
(function (BusinessAccessPolicyModeV2) {
    BusinessAccessPolicyModeV2["BUSINESS_ONLY"] = "BUSINESS_ONLY";
    BusinessAccessPolicyModeV2["USER_REQUIRED"] = "USER_REQUIRED";
    BusinessAccessPolicyModeV2["HYBRID"] = "HYBRID";
})(BusinessAccessPolicyModeV2 || (BusinessAccessPolicyModeV2 = {}));
export const BusinessProductAreaV2Schema = z.nativeEnum(BusinessProductAreaV2);
export const BusinessActionEnumSchema = z.nativeEnum(BusinessActionEnum);
export const BusinessPaymentMethodV2Schema = z.nativeEnum(BusinessPaymentMethodV2);
export const BusinessAccessPolicyModeV2Schema = z.nativeEnum(BusinessAccessPolicyModeV2);
